import { useEffect, useRef, useState } from 'react';

import { CustomRecurrenceFields, RepeatPreset } from '../types';
import { buildCronExpression, createDefaultCustomFields, parseCronExpression } from '../utils/cron';

interface UseRecurrenceEditorParams {
  /** `undefined` when the schedule is `scheduled` (i.e. the "Repetir" preset is implicitly `never`). */
  expression: string | undefined;
  startDate: Date;
  onChange: (preset: RepeatPreset, expression: string | undefined) => void;
}

/**
 * Bridges the guided "Repetir" editor (preset + custom fields) with the raw cron `expression`
 * stored in the schedule value. Falls back to "advanced" (raw expression editing) when the current
 * expression doesn't match a pattern the guided editor can represent.
 *
 * The selected preset is tracked locally rather than re-derived from `expression` on every render,
 * so intermediate/ambiguous states (e.g. a `custom` config that momentarily matches a simpler
 * preset's cron shape) don't get silently reclassified. Re-parsing only happens when `expression`
 * changes from an *external* source (e.g. a different saved value being loaded), not from this
 * hook's own `onChange` calls.
 */
export function useRecurrenceEditor({
  expression,
  startDate,
  onChange,
}: UseRecurrenceEditorParams) {
  const initialParsed = expression ? parseCronExpression(expression, startDate) : null;

  const lastEmittedExpression = useRef(expression);
  const [preset, setPresetState] = useState<RepeatPreset>(() =>
    !expression ? 'never' : (initialParsed?.preset ?? 'custom'),
  );
  const [custom, setCustom] = useState<CustomRecurrenceFields>(
    () => initialParsed?.custom ?? createDefaultCustomFields(startDate),
  );
  const [isAdvanced, setIsAdvanced] = useState(() => Boolean(expression) && initialParsed === null);

  useEffect(() => {
    if (expression === lastEmittedExpression.current) return;
    lastEmittedExpression.current = expression;

    if (!expression) {
      setPresetState('never');
      return;
    }

    const parsed = parseCronExpression(expression, startDate);
    setIsAdvanced(parsed === null);
    setPresetState(parsed?.preset ?? 'custom');
    if (parsed) setCustom(parsed.custom);
  }, [expression, startDate]);

  function emit(nextPreset: RepeatPreset, nextCustom: CustomRecurrenceFields) {
    if (nextPreset === 'never') {
      lastEmittedExpression.current = undefined;
      onChange('never', undefined);
      return;
    }
    const nextExpression = buildCronExpression(nextPreset, nextCustom, startDate);
    lastEmittedExpression.current = nextExpression;
    onChange(nextPreset, nextExpression);
  }

  function setPreset(nextPreset: RepeatPreset) {
    setPresetState(nextPreset);
    setIsAdvanced(false);
    emit(nextPreset, custom);
  }

  function updateCustom(partial: Partial<CustomRecurrenceFields>) {
    const nextCustom = { ...custom, ...partial };
    setCustom(nextCustom);
    emit('custom', nextCustom);
  }

  function setRawExpression(nextExpression: string) {
    lastEmittedExpression.current = nextExpression;
    onChange('custom', nextExpression);
  }

  return {
    preset,
    custom,
    isAdvanced,
    setIsAdvanced,
    setPreset,
    updateCustom,
    setRawExpression,
  };
}
